import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "@aws-amplify/ui-react/styles.css";

import { Dayjs } from "dayjs";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid2 as Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import SaveIcon from "@mui/icons-material/Save";
import {
  CountryCode,
  Registration,
  State,
  Timezone,
  UpdateBaptismInput,
  USAddress,
  ValidationError,
} from "baptize-america-client";

import { useAlert } from "../../components/AlertProvider";
import ConfirmDialog from "../../components/ConfirmDialog";
import TimePicker from "../../components/TimePicker";
import {
  deleteBaptism,
  deleteRegistration,
  getBaptism,
  updateBaptism,
} from "../../utils/client";
import {
  dateFromDateTimeTzStr,
  dayJsTimeFromDateTx,
  downloadCSV,
  humaneReadableTime,
  isAdmin,
  isChurchManager,
  stringFromDateTz,
} from "../../utils/helper";
import DebouncedButton from "../../components/DebounceButton";
import TopNavBar from "../../components/TopNavBar";
import { useUserData } from "../../components/DataProvider";

const styles = {
  titleBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mb: 5,
  },
  innerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    mt: 2,
    mb: 2,
  },
  itemContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    mt: 2,
    mb: 2,
    flexGrow: 1,
  },
  updateButtonBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mt: 5,
  },
  updateButton: {
    backgroundColor: "#00169E",
    color: "#FFF",
    justifyContent: "center",
    height: 50,
    width: 300,
    fontSize: 16,
  },
  attendee: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    mt: 1,
    mb: 1,
  },
  infoBox: {
    bgcolor: "rgba(123, 123, 123, 0.2)",
    width: "100%",
    p: 1,
    borderRadius: 2,
  },
  infoIcon: {
    display: "flex",
    alignSelf: "center",
    mr: 1,
    color: "rgba(123, 123, 123, 1)",
  },
};

const ManageBaptismEvent = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { churchId, baptismId } = useParams();
  const { attributes } = useUserData();
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const [location, setLocation] = useState<USAddress>({
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: undefined,
    postalCode: "",
    country: CountryCode.US,
  });
  const [note, setNote] = useState<string>(" "); //Empty space fixes render bug in mui on mobile
  const [date, setDate] = useState<string>();
  const [time, setTime] = useState<Dayjs>();
  const [timezone, setTimezone] = useState<Timezone>();
  const [attendees, setAttendees] = useState<Registration[]>();
  const [removeBaptism, setRemoveBaptism] = useState<boolean>(false);
  const [removeAttendee, setRemoveAttendee] = useState<Registration>();
  const [errors, setErrors] = useState<{
    note?: string;
    streetAddress1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    date?: string;
    time?: string;
    timezone?: string;
  }>({});

  useEffect(() => {
    getBaptism({
      churchId: churchId,
      baptismId: baptismId,
    }).then((res) => {
      setLocation(res?.address!!);
      setNote(res?.note ?? "");
      setTime(dayJsTimeFromDateTx(res.time!!, res.timezone!!));
      setTimezone(res?.timezone);
      setDate(stringFromDateTz(res?.time!, res?.timezone!));
      setAttendees(res.registrations);
    });
  }, [churchId, baptismId]);

  const validUser =
    isChurchManager(attributes, churchId) || isAdmin(attributes);

  const validateInputs = () => {
    const newErrors: Record<string, string> = {};
    if (!location.streetAddress1?.trim())
      newErrors.streetAddress1 = "Address is required.";
    if (!location.city?.trim()) newErrors.city = "City is required.";
    if (!location.state?.trim()) newErrors.state = "State is required.";
    if (!timezone?.trim()) newErrors.timezone = "Timezone is required.";
    if (!location.postalCode?.trim())
      newErrors.postalCode = "Zip Code is required.";
    if (!note?.trim()) newErrors.note = "Note is required.";
    if (!date?.trim()) newErrors.date = "Date is required.";
    if (!time) newErrors.time = "Time is required.";
    if (!timezone?.trim()) newErrors.timezone = "Time Zone is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleUpdateBaptism = async (input: UpdateBaptismInput) => {
    if (!validateInputs()) return;
    updateBaptism(input).then(
      (res) => {
        setAlert({
          showAlert: true,
          alertMsg: "Updated baptism event",
          alertType: "success",
        });
        navigate(`/churches/${res.churchId}`, { replace: true });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error updating baptism event",
          alertType: "error",
        });
      },
    );
  };

  const handleDeleteBaptism = async (churchId: string, baptismId: string) => {
    deleteBaptism({ churchId, baptismId }).then(
      (res) => {
        navigate(`/churches/${res.churchId}`, { replace: true });
        setAlert({
          showAlert: true,
          alertMsg: "Baptism Event removed",
          alertType: "success",
        });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error removing event",
          alertType: "error",
        });
      },
    );
  };

  const handleRemoveAttendee = async (registration: Registration) => {
    deleteRegistration({
      churchId: churchId,
      baptismId: baptismId,
      sub: registration.sub,
    }).then(
      () => {
        setAttendees((prevState) => {
          return prevState?.filter((it) => it.sub !== registration.sub);
        });
        setAlert({
          showAlert: true,
          alertMsg: `Attendee ${registration.firstName} ${registration.lastName} has been removed from this event`,
          alertType: "warning",
        });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error removing attendee",
          alertType: "error",
        });
      },
    );
  };

  return (
    <Container maxWidth={isLargeScreen ? "md" : false}>
      <Box sx={styles.titleBox} maxWidth={theme.breakpoints.values.md}>
        <TopNavBar showBackArrow={true} />
      </Box>
      <Box sx={styles.innerBox} minWidth={theme.breakpoints.values.sm}>
        <Typography
          align={"left"}
          variant={"h5"}
          fontFamily={"AmericanGrotesk-Bold"}
        >
          Manage Baptism
        </Typography>
        {validUser && (
          <Button
            sx={{ borderRadius: 3, backgroundColor: "#d32f2f", color: "#FFF" }}
            variant={"outlined"}
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setRemoveBaptism(true)}
          >
            Delete
          </Button>
        )}
      </Box>
      <Box sx={[styles.infoBox, { mb: 1.5 }]}>
        <Typography
          align={"left"}
          sx={{ display: "flex", flexDirection: "row" }}
        >
          <InfoIcon sx={styles.infoIcon} />
          Baptism Events can be at your church or a local body of water.
        </Typography>
      </Box>
      <Divider orientation="horizontal" />
      <Stack
        direction="column"
        sx={{ justifyContent: "space-between", display: "flex" }}
      >
        <TextField
          required={true}
          disabled={!validUser}
          id={"outlined-required"}
          label={"Street Address"}
          value={location?.streetAddress1}
          error={!!errors.streetAddress1}
          helperText={errors.streetAddress1}
          onChange={(e) =>
            setLocation({
              ...location,
              streetAddress1: e.target.value,
            } as USAddress)
          }
          sx={styles.itemContainer}
        />
        <TextField
          required={false}
          disabled={!validUser}
          id={"outlined-required"}
          label={"Unit/Suite/Other"}
          value={location?.streetAddress2}
          onChange={(e) =>
            setLocation({
              ...location,
              streetAddress2: e.target.value,
            } as USAddress)
          }
          sx={styles.itemContainer}
        />
      </Stack>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", display: "flex" }}
      >
        <TextField
          required={true}
          disabled={!validUser}
          id={"outlined-required"}
          label={"City"}
          value={location?.city}
          error={!!errors.city}
          helperText={errors.city}
          onChange={(e) =>
            setLocation({ ...location, city: e.target.value } as USAddress)
          }
          sx={[styles.itemContainer]}
        />
        <TextField
          required={true}
          select
          disabled={!validUser}
          id={"state-select"}
          label={"State"}
          value={location?.state}
          error={!!errors.state}
          helperText={errors.state}
          onChange={(e) =>
            setLocation({
              ...location,
              state: e.target.value as State,
            } as USAddress)
          }
          sx={[
            styles.itemContainer,
            { ml: 1, mr: 1, maxWidth: "20vw", minWidth: "18vw" },
          ]}
          slotProps={{
            select: {
              native: true,
            },
          }}
        >
          {Object.keys(State).map((option, index) => (
            <option key={`Select_${option}_${index}`} value={option}>
              {option}
            </option>
          ))}
        </TextField>
        <TextField
          required={true}
          disabled={!validUser}
          id={"outlined-required"}
          label={"Zip Code"}
          value={location?.postalCode}
          error={!!errors.postalCode}
          helperText={errors.postalCode}
          onChange={(e) =>
            setLocation({
              ...location,
              postalCode: e.target.value,
            } as USAddress)
          }
          sx={[styles.itemContainer]}
        />
      </Stack>
      <Grid
        container
        direction="row"
        spacing={0} // Adjust the spacing between Grid items
        justifyContent="space-between"
        alignItems="stretch"
      >
        <Grid size={4} component="div" display={"flex"}>
          <TextField
            required={true}
            select
            disabled={!validUser}
            id={"select-date"}
            label={"Select Date"}
            value={date ?? ""}
            error={!!errors.date}
            helperText={errors.date}
            onChange={(e) => {
              setDate(e.target.value);
            }}
            sx={{ mr: 1, width: "100%" }}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              select: {
                displayEmpty: true,
                native: true,
              },
            }}
          >
            <option value="" disabled>
              Select Date
            </option>
            <option key={`Select_${"2025-06-07"}`} value={"2025-06-07"}>
              {"Saturday (6/7/2025)"}
            </option>
            <option key={`Select_${"2025-06-08"}`} value={"2025-06-08"}>
              {"Sunday (6/8/2025)"}
            </option>
          </TextField>
        </Grid>
        <Grid size={4} component="div" display={"flex"}>
          <TextField
            required={true}
            select
            disabled={!validUser}
            id={"tz-select"}
            label={"TimeZone"}
            value={timezone ?? ""}
            error={!!errors.timezone}
            helperText={errors.timezone}
            onChange={(e) => {
              setTimezone(e.target.value as Timezone);
              setTime(undefined); // force user to click time again
            }}
            sx={{ mr: 1, width: "100%" }}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              select: {
                displayEmpty: true,
                native: true,
              },
            }}
          >
            <option value="" disabled>
              Select TimeZone
            </option>
            {Object.keys(Timezone)
              .filter((it) => it !== "UTC")
              .map((option, index) => (
                <option
                  key={`Select_${option}_${index}`}
                  value={Timezone[option as keyof typeof Timezone]}
                >
                  {option}
                </option>
              ))}
          </TextField>
        </Grid>
        <Grid size={4} component="div" display={"flex"}>
          <TimePicker
            sx={{ width: "100%" }}
            timezone={timezone ?? "system"}
            label={"Service Time"}
            value={time ?? null}
            handleChange={(value: Dayjs) => {
              if (!value) {
                setErrors({ ...errors, time: "Time is required." });
              } else {
                setErrors({ ...errors, time: "" });
                setTime(value);
              }
            }}
            disabled={!validUser}
            timeError={errors.time}
          />
        </Grid>
      </Grid>
      <TextField
        multiline
        required
        rows={6}
        disabled={!validUser}
        value={note}
        label={"Notes & Additional Info"}
        error={!!errors.note}
        helperText={errors.note}
        onChange={(e) => setNote(e.target.value)}
        sx={[styles.itemContainer, { mt: 3 }]}
      />
      {validUser && (
        <Box sx={styles.updateButtonBox}>
          <DebouncedButton
            variant="contained"
            sx={styles.updateButton}
            endIcon={<SaveIcon />}
            onClick={() => {
              if (!validateInputs()) return;
              handleUpdateBaptism({
                churchId: churchId,
                baptismId: baptismId,
                time: dateFromDateTimeTzStr(
                  date ?? "",
                  time?.format("HH:mm:ss") ?? "",
                  timezone ?? "",
                ),
                timezone: timezone,
                address: location,
                note: note,
              });
            }}
          >
            {"Update"}
          </DebouncedButton>
        </Box>
      )}
      <div>
        <Box sx={styles.innerBox}>
          <Typography align={"left"} variant={"h6"}>
            {`Attendees (${attendees?.length ?? 0})`}
          </Typography>
          <DebouncedButton
            disabled={(attendees?.length ?? 0) === 0}
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (!validateInputs()) return;
              downloadCSV(
                attendees ?? [],
                humaneReadableTime(
                  dateFromDateTimeTzStr(
                    date ?? "",
                    time!!.format("HH:mm:ss"),
                    timezone ?? "",
                  )!!,
                  timezone!!,
                ),
              );
            }}
          >
            Export to CSV
          </DebouncedButton>
        </Box>
        <Divider sx={{ mt: 1 }} orientation="horizontal" />
        {(attendees ?? []).map((attendee) => (
          <div key={attendee.sub}>
            <Box sx={styles.attendee}>
              <Typography align={"left"} sx={{ color: "#0103077F" }}>
                Name:
              </Typography>
              <Box
                alignSelf={"right"}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Typography>{`${attendee.firstName} ${attendee.lastName}`}</Typography>
                <IconButton onClick={() => setRemoveAttendee(attendee)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
            </Box>
            <Box sx={styles.attendee}>
              <Typography align={"left"} sx={{ color: "#0103077F" }}>
                Email:
              </Typography>
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                align={"right"}
              >
                {attendee.email}
              </Typography>
            </Box>
            <Divider orientation="horizontal" />
          </div>
        ))}
        <ConfirmDialog
          open={!!removeAttendee}
          onClose={() => setRemoveAttendee(undefined)}
          onConfirm={() => handleRemoveAttendee(removeAttendee!!)}
          title={`Remove ${removeAttendee?.firstName} ${removeAttendee?.lastName}?`}
          content={`Are you sure you want to remove ${removeAttendee?.firstName} ${removeAttendee?.lastName} as a attendee?`}
        />
      </div>
      <ConfirmDialog
        open={removeBaptism}
        onClose={() => setRemoveBaptism(false)}
        onConfirm={() => handleDeleteBaptism(churchId!!, baptismId!!)}
        title={`Delete Baptism Event?`}
        content={`Are you sure you want to remove this Baptism Event?`}
      />
    </Container>
  );
};

export default ManageBaptismEvent;
